// eslint-disable-file @typescript-eslint/no-namespace

import {
    ArtifactStatus,
    ConferenceConnectionData as ConferenceConnectionDataCommon,
    ConferencesHistoryItem,
    PeerInfo as PeerInfoCommon,
    PeerState as PeerStateCommon,
    PeerType,
    PermissionsInfo as PermissionsInfoCommon,
} from '../../../../../../common/types/conferences';
import {
    AvatarData as AvatarDataCommon,
    Reaction as ReactionCommon,
    UserData as UserDataCommon,
} from '../../../../../../common/types/user-info';
import {
    ConferenceStateInfo,
    EmotionType,
    EmptyObject,
    GetMeUserResponse,
    ParticipantInfo,
    ReactionReason,
    ReactionType,
    SummarizationMailRecipient,
    TelemostAgent,
} from '../../../../types';
import { Role, RolePermission, RolePermissionMatrixSlice } from '../../permissions';
import {
    StatsEventReviewShow,
    StatsEventReviewSubmit,
    StatsEventSupportFromConferenceShow,
    StatsEventSupportFromConferenceSubmit,
    StatsEventSupportShow,
} from '../../stats';

export enum ARTIFACT {
    RECORDING = 'RECORDING',
    SUMMARIZATION = 'SUMMARIZATION',
    MEETING_REPORT = 'MEETING_REPORT',
    WITHOUT_ARTIFACTS = 'WITHOUT_ARTIFACTS',
}

export enum ArtifactError {
    RECORDING_TOO_SHORT = 'RECORDING_TOO_SHORT',
    ACCESS_FORBIDDEN_CONTACT_OWNER = 'ACCESS_FORBIDDEN_CONTACT_OWNER',
    ACCESS_FORBIDDEN_CONTACT_SUPPORT = 'ACCESS_FORBIDDEN_CONTACT_SUPPORT',
    INSUFFICIENT_DISK_SPACE = 'INSUFFICIENT_DISK_SPACE',
    OTHER = 'OTHER',
}

export { ArtifactStatus, PeerType };

export declare namespace CloudAPI {
    type ConferenceConnectionData = ConferenceConnectionDataCommon;
    interface MatrixRole {
        role: Role;
        allowed: RolePermission[];
    }

    interface MatrixDto {
        preset_id: string;
        roles: MatrixRole[];
    }

    interface MatrixBodyRole {
        role: Role;
        allowed: RolePermissionMatrixSlice;
    }

    interface MatrixBody {
        preset_id?: string;
        roles: MatrixBodyRole[];
    }

    namespace SendCommand {
        type ApiCommand =
            | 'mute-microphone'
            | 'mute-camera'
            | 'mute-desktop'
            | 'mute-all-microphones'
            | 'mute-all-cameras'
            | 'kick'
            | 'set-user-role'
            | 'close-room'
            | 'mute-all-recordings-permanent'
            | 'mute-reaction'
            | 'mute-all-reactions'
            | 'mute-recording'
            | 'mute-all-recordings'
            | 'start-cloud-recording'
            | 'stop-cloud-recording'
            | 'grant-all-cloud-recording'
            | 'start-summarization'
            | 'stop-summarization'
            | 'grant-all-summarization'
            | 'grant-all-calendar-summarization-receive';

        interface Request {
            command: ApiCommand;
            peerId?: string;
            excludePeerId?: string;
            targetUid?: string;
            role?: string;
            withBan?: boolean;
            allow?: boolean;
            mailRecipient?: SummarizationMailRecipient;
            withoutRemoval?: boolean;
        }
    }

    namespace FeedbackWasShown {
        type Request = Omit<StatsEventReviewShow, 'event_type'>;
        type Response = EmptyObject;
    }

    namespace SendFeedback {
        type Request = Omit<StatsEventReviewSubmit, 'event_type'>;
        type Response = EmptyObject;
    }

    namespace SupportFormWasShown {
        type Request = Omit<StatsEventSupportShow, 'event_type'>;
        type Response = EmptyObject;
    }

    namespace ReportAProblemFormWasShown {
        type Request = Omit<StatsEventSupportFromConferenceShow, 'event_type'>;
        type Response = EmptyObject;
    }

    namespace SendReportAProblem {
        type Request = Omit<StatsEventSupportFromConferenceSubmit, 'event_type'>;
        type Response = EmptyObject;
    }

    namespace CreateConference {
        interface Request {
            access_level?: string;
        }

        type Response = ConferenceConnectionData;
    }

    namespace ConnectToConference {
        type Request = {
            roomId: string;
            telemostHost: string;
            searchString?: string;
            displayName?: string;
            enableGoloom?: boolean;
            isGoloomRc?: boolean;
            additionalHeaders?: Record<string, string>;
            appVersion: string;
        };

        type Response = ConferenceConnectionData;
    }

    namespace CreateBroadcast {
        interface Request {
            caption?: string;
            description?: string;
            access_level?: string;
        }

        interface Response {
            broadcast_uri: string;
        }
    }

    namespace StartBroadcast {
        interface Response {
            started_at: number;
            owner_uid: string;
            stream_uri: string;
        }
    }

    namespace StopBroadcast {
        interface Response {
            started_at: number;
            stopped_at: number;
            owner_uid: string;
        }
    }

    namespace FetchParticipantsInfos {
        interface Response {
            items: ParticipantInfo[];
        }
    }

    export interface LeaveRoomOptions {
        peerId: string;
        peerToken: string;
        mediaSessionId: string;
    }

    namespace RefreshOffer {
        interface Request {
            roomId: string;
            peerId: string;
            peerToken: string;
            mediaSessionId: string;
        }
        type Response = EmptyObject;
    }

    type AvatarData = AvatarDataCommon;

    type UserData = UserDataCommon;

    type Reaction = ReactionCommon;

    type PeerState = PeerStateCommon;

    type PeerInfo = PeerInfoCommon;

    type PermissionsInfo = PermissionsInfoCommon;

    namespace GetPeersState {
        interface Request {
            peers: {
                peer_id: string;
                version?: number;
            }[];
            conference?: {
                version: number;
            };
            permissions?: {
                version?: number;
            };
        }

        interface Response {
            peers: PeerInfo[];
            conference?: ConferenceStateInfo;
            permissions?: PermissionsInfo;
        }
    }

    namespace SetPeerState {
        interface Request {
            is_local_recording?: boolean;
        }
        type Response = void;
    }

    namespace SetPeerReaction {
        interface Request {
            reaction_type: ReactionType | null;
            reason: ReactionReason;
        }
        type Response = void;
    }

    namespace SetPeerEmotion {
        interface Request {
            emotion_type: EmotionType;
        }
        type Response = void;
    }

    namespace SetSummarizationSettings {
        interface Request {
            mail_recipient: SummarizationMailRecipient;
        }
        type Response = void;
    }

    namespace SendFeedbackToSupport {
        interface Query {
            reply_email: string;
            subject: TelemostAgent;
            app_version: string;
            os_version: string;
            recipient_type: 'supports' | 'testers';
        }
        interface Request {
            fos_support_text: string;
            debug_info: {
                login: string;
                browser: string;
                browser_version: number;
                os_family: string;
                os_version: number;
                telemost_version: string;
                room_id: string;
                conference_uri: string;
                peer_id: string;
                media_platform: string;
                time: string;
            };
        }
    }

    namespace WaitingRoomCheckAccess {
        interface Response {
            admitted: boolean;
        }
    }

    interface WaitingRoomPeerState {
        peer_id: string;
        version: number;
        waiting_room_entered_at: number;
        state: UserData;
        peer_type: PeerType;
    }

    namespace GetWaitingRoomPeers {
        interface Response {
            peers: WaitingRoomPeerState[];
        }
    }

    namespace SetViewInBroadcast {
        type Response = EmptyObject;
    }

    namespace GetChatHistory {
        interface Response {
            timestamp_from: number;
            timestamp_to: number;
            history: string;
        }
    }

    namespace GetMeUser {
        type Response = GetMeUserResponse;
    }

    namespace GetMatrix {
        interface Response extends MatrixDto {
            non_configurable_permissions: [];
            role_permissions_have_changed_from_default: boolean;
        }
    }

    namespace SetMatrix {
        type Request = MatrixBody;
    }

    namespace ConferencesHistory {
        type Request = {
            from?: string;
            to?: string;
            artifact?: ARTIFACT;
        };
        type Response = {
            iteration_key?: string;
            limit: number;
            items: ConferencesHistoryItem[];
        };
    }

    namespace ConferenceArtifacts {
        type Recipient = {
            role: string;
            email?: string;
            display_name: string;
        };
        type Artifact = {
            id?: string;
            started_at?: string;
            stopped_at?: string;
            am_i_recipient?: boolean;
            link?: { href: string };
            // для записей на устройство
            recipient?: Recipient;
            // для всех остальных видов артефактов
            recipients?: Recipient[];
            recipients_total?: number;
            status?: ArtifactStatus;
            error?: ArtifactError;
        };
        type Response = {
            summarizations: Artifact[];
            cloud_recordings: Artifact[];
            local_recordings: Artifact[];
        };
    }

    namespace ConferenceArtifactsRecipients {
        type Response = {
            total: number;
            offset: number;
            limit: number;
            items: ConferenceArtifacts.Recipient[];
        };
    }
}
